import React, { FC } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import RosteringInformationAnnouncement from './components/RosteringInformationAnnouncement';
import OrdersList from './components/OrderList';
import { useQuery } from '@apollo/client';
import PageContainer from 'components/@layout/PageContainer';
import ActivateMastercodeModal from './modals/ActivateMastercodeModal';
import TransferMastercodeModal from './modals/TransferMastercodeModal';
import useUiStore from 'store/ui';
import useUserStore from 'store/user';
import { Mastercode, Order } from 'types/schema-generated-types';
import GET_MY_ACTIVE_ORDERS from 'graph/orders/get-my-orders-query';
import GET_UNAUTHENTICATED_ORDER from 'graph/orders/get-unauthenticated-order';
import { useHistory } from 'react-router-dom';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(4)
    },
    ordersContainer: {
      marginTop: theme.spacing(4)
    },
    ordersHeader: {
      marginBottom: theme.spacing(1)
    }
  };
});

interface OrdersProps {
  orderId?: number | null;
  ownerEmail?: string | Array<string> | null;
}

export type ActivateClickHandler = (
  order: Order,
  mastercode: Mastercode | null,
  activateAll: boolean
) => () => void;

export type TransferClickHandler = (
  order: Order,
  mastercode: Mastercode | null,
  transferAll: boolean
) => () => void;

const Orders: FC<OrdersProps> = ({ orderId, ownerEmail }) => {
  const classes = useStyles();
  const history = useHistory();
  const [, { openQuarterModal, closeModal }] = useUiStore();
  const [{ me }] = useUserStore();

  const ordersQuery = orderId ? GET_UNAUTHENTICATED_ORDER : GET_MY_ACTIVE_ORDERS;
  const ordersVariable = orderId ? { orderId, ownerEmail } : { where: { completed: false } };
  const { data, loading, error, refetch } = useQuery(ordersQuery, {
    variables: ordersVariable,
    fetchPolicy: 'network-only'
  });

  const onActivateMastercodeClick = React.useCallback<ActivateClickHandler>(
    (order, mastercode, activateAll) => (): void => {
      if (orderId) {
        history.push('/dashboard/orders');
      } else {
        openQuarterModal({
          body: (
            <ActivateMastercodeModal
              order={order}
              mastercode={mastercode}
              onClose={closeModal}
              onSubmit={refetch}
              activateAll={activateAll}
            />
          )
        });
      }
    },
    [orderId, history, openQuarterModal, closeModal, refetch]
  );

  const onTransferMastercodeClick = React.useCallback(
    (order: Order, mastercode: Mastercode | null, transferAll: boolean) => (): void => {
      openQuarterModal({
        body: (
          <TransferMastercodeModal
            order={order}
            ownerEmail={ownerEmail}
            mastercode={mastercode}
            onClose={closeModal}
            onSubmit={refetch}
            transferAll={transferAll}
          />
        )
      });
    },
    [ownerEmail, openQuarterModal, closeModal, refetch]
  );

  let orders: Order[] = [];
  if (data?.me) {
    orders = data.me.orders ?? [];
  } else if (data?.order) {
    orders = [data.order];
  }

  return (
    <PageContainer loading={loading} error={error}>
      <div className={classes.container}>
        {me && <RosteringInformationAnnouncement />}
        {orders.length === 0 ? (
          <EmptyResultSetMessage
            style={{ marginTop: '12px' }}
            message={'No Orders to Activate'}
            subtitle={'You have no new orders to activate or transfer.'}
          />
        ) : (
          <div className={classes.ordersContainer}>
            <Typography className={classes.ordersHeader} variant="h2">
              Orders
            </Typography>
            <OrdersList
              orders={orders}
              onActivateMastercodeClick={onActivateMastercodeClick}
              onTransferMastercodeClick={onTransferMastercodeClick}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default Orders;
